import axios, {
  type AxiosInstance,
  AxiosError,
  type AxiosRequestConfig,
} from 'axios'
import { useToast } from '@/composables/useToast'

/**
 * Один общий axios-инстанс для всего admin-API.
 *
 * Все запросы автоматически:
 *   1. Идут на VITE_API_BASE_URL (по умолчанию /api — за Caddy).
 *   2. Несут заголовок Authorization: Bearer <access_token> из Pinia-store.
 *   3. При 401 — пытаются обновить токен через /auth/refresh; если не получилось,
 *      сбрасывают сессию и редиректят на /login (обрабатывает router-guard).
 */
export const baseURL = import.meta.env.VITE_API_BASE_URL || '/api'

/**
 * Расширение AxiosRequestConfig для управления глобальным тостом ошибки.
 *
 * По умолчанию любая 4xx/5xx (кроме 401) показывает toast.error() через
 * response-interceptor. Иногда это лишнее — например, на «опциональных»
 * GET-ах, где 404 = «фичи ещё нет на бэке» (свежевыкаченный SPA против
 * не до-конца обновлённого admin-API): такие случаи мы хотим ловить
 * локально (через try/catch + EmptyState), без шумного красного попапа
 * на пол-экрана.
 *
 * Использование:
 *   http.get('/broadcasts/templates', { skipErrorToast: true })
 *   http.get('/foo', { skipErrorToast: (status) => status === 404 })
 *
 * Локальный catch при этом всё равно срабатывает и может уже сам решить,
 * показывать ли тост (например, для 500-ого).
 */
export type SkipErrorToast = boolean | ((status: number) => boolean)

export interface AdminRequestConfig extends AxiosRequestConfig {
  skipErrorToast?: SkipErrorToast
}

export const http: AxiosInstance = axios.create({
  baseURL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
  // Массивы в query — повтором имени (`statuses=a&statuses=b`), как
  // ожидает FastAPI. Дефолт axios (`statuses[]=a`) бэкенд молча
  // игнорирует: из-за этого, например, фильтр статусов eSIM не работал.
  paramsSerializer: { indexes: null },
}) as AxiosInstance

let accessTokenGetter: (() => string | null) | null = null
let refreshHandler: (() => Promise<boolean>) | null = null
let onAuthFailed: (() => void) | null = null

export function registerAuthHooks(opts: {
  getAccessToken: () => string | null
  refresh: () => Promise<boolean>
  onFail: () => void
}): void {
  accessTokenGetter = opts.getAccessToken
  refreshHandler = opts.refresh
  onAuthFailed = opts.onFail
}

http.interceptors.request.use((config) => {
  const token = accessTokenGetter?.()
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`)
  }
  return config
})

let refreshing: Promise<boolean> | null = null

/**
 * Достаёт человекочитаемое сообщение из 4xx/5xx-ответа FastAPI:
 *   - {"detail": "string"}                  → "string"
 *   - {"detail": [{"loc": [...], "msg":...}]} (422) → "loc.path: msg"
 *   - всё прочее                            → method + status + URL
 */
function describeError(error: AxiosError<any>): string {
  const status = error.response?.status
  const data: any = error.response?.data
  if (data) {
    if (typeof data.detail === 'string') return data.detail
    if (Array.isArray(data.detail)) {
      return data.detail
        .map((d: any) => `${(d.loc ?? []).slice(1).join('.')}: ${d.msg}`)
        .join('\n')
    }
    if (typeof data === 'string' && data.length < 300) return data
  }
  if (status === 0 || error.code === 'ERR_NETWORK') return 'Нет связи с сервером.'
  return `${error.config?.method?.toUpperCase() ?? 'HTTP'} ${error.config?.url ?? ''} → ${status ?? 'error'}`
}

http.interceptors.response.use(
  (r) => r,
  async (error: AxiosError<any>) => {
    const status = error?.response?.status
    const original = error?.config as any
    if (status === 401 && original && !original._retry && refreshHandler) {
      original._retry = true
      try {
        if (!refreshing) {
          refreshing = refreshHandler()
        }
        const ok = await refreshing
        refreshing = null
        if (ok) {
          return http(original)
        }
      } catch {
        refreshing = null
      }
      onAuthFailed?.()
      // На 401 после неудачного refresh не сыпем тостом — пользователь
      // и так редиректится на /login через guard.
      return Promise.reject(error)
    }

    // Любая другая ошибка: показываем тост, чтобы пользователь видел,
    // что именно пошло не так (раньше формы «молча» падали — нечего
    // отлаживать без devtools).
    //
    // Запросы могут отключить глобальный тост через
    // `{ skipErrorToast: true }` (булево) или указать предикат по
    // статусу (`(status) => status === 404`) — это нужно для «мягких»
    // GET-ов, где локальный код сам решит, ругаться ли. См.
    // SkipErrorToast / AdminRequestConfig выше.
    // У бинарных запросов (`responseType: 'blob'` — выгрузка отчётов в
    // XLSX/PDF) тело ошибки тоже приходит блобом. Распаковываем его в
    // JSON, иначе вместо понятного `detail` от FastAPI пользователь
    // увидит бесполезное «POST /esims/... → 500».
    if (error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text()
        error.response.data = text.trimStart().startsWith('{')
          ? JSON.parse(text)
          : text
      } catch {
        /* нечитаемый блоб — describeError отдаст generic-сообщение */
      }
    }

    const skip = (original as AdminRequestConfig | undefined)?.skipErrorToast
    const suppress =
      skip === true ||
      (typeof skip === 'function' && status !== undefined && skip(status))

    if (!suppress && status && status >= 400 && status !== 401) {
      try {
        useToast().error(describeError(error))
      } catch {
        // useToast() может бросить, если Pinia/Vue ещё не подняты —
        // безопасно игнорируем.
      }
    }
    return Promise.reject(error)
  },
)
